import React, { FC, ReactNode, useCallback, useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import dayjs from "dayjs";
import { logoutUser } from "../../../store/thunks/authenticationThunk";
import { portalManagementReset } from "../../../store/features/portalManagement/portalManagementSlice";
import { logout } from "../../../store/features/authentication/authenticationSlice";
import { persistor } from "../../../store";

const LayoutContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

interface Props {
  children: ReactNode;
}

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

const Layout: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, loggedIn } = useAppSelector(
    (state: any) => state.authentication
  );

  let timeStamp: any;
  let warningInactiveInterval: any = useRef();
  let startTimerInterval: any = useRef();

  let warningInactive = (timeString: any) => {
    clearTimeout(startTimerInterval.current);

    warningInactiveInterval.current = setInterval(() => {
      const currenttimestamp = dayjs();
      const diff = currenttimestamp.diff(dayjs(timeString), "minute", true);
      if (diff > 30) {
        clearInterval(warningInactiveInterval.current);
        sessionStorage.removeItem("lastTimeStamp");
        dispatch(logoutUser({ user_index: user.client_user_index }));
        dispatch(portalManagementReset());
        dispatch(logout());
        persistor.purge();
        navigate("/login");
      }
    }, 1000 * 60 * 5);
  };

  let timeChecker = () => {
    startTimerInterval.current = setTimeout(() => {
      let storedTimeStamp = sessionStorage.getItem("lastTimeStamp");
      warningInactive(storedTimeStamp);
    }, 1000 * 60 * 5);
  };

  let resetTimer = useCallback(() => {
    clearTimeout(startTimerInterval.current);
    clearInterval(warningInactiveInterval.current);

    if (loggedIn) {
      timeStamp = dayjs();
      sessionStorage.setItem("lastTimeStamp", timeStamp);
    } else {
      clearInterval(warningInactiveInterval.current);
      sessionStorage.removeItem("lastTimeStamp");
    }
    timeChecker();
  }, [loggedIn]);

  useEffect(() => {
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });
    return () => {
      clearTimeout(startTimerInterval.current);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer, timeChecker]);

  return (
    <LayoutContainer>
      <Outlet />
    </LayoutContainer>
  );
};

export default Layout;
